import { Injectable } from '@angular/core';
import {
  IAudit,
  IDoctor,
  IErpOrder,
  IHospital,
  IInstitution,
  IIntegratedCare,
  IModel,
  INursingHome,
  INursingService,
  IPatient,
  IPatientNote,
  IPharmacy,
  ISingleOrder,
} from '@pia/pia.shared';
import moment from 'moment';
import { AppController } from 'src/app/common/controller/app-controller';
import { ObservableModel } from 'src/app/common/viewmodel/observable-model';
import { AttachmentDto } from 'src/app/shared/models/attachment/attachment-dto.model';
import { IDatabaseSynchronizer } from 'src/app/shared/services/contracts/sync/database-synchronizer_T';

@Injectable()
export class EmergencyPatientDataSaver {
  private _cancellationToken = {
    cancelled: new ObservableModel<boolean>(false, false),
    promise: undefined,
    syncCompleted: {},
    serviceRepeats: {},
    cancel: () => undefined,
    reset: () => undefined,
  };

  constructor(private _appController: AppController) {}

  public async save(patientData: any): Promise<void> {
    await this.savePatientData(patientData);
    await this.saveInstitutionData(patientData);
  }

  private async savePatientData(patientData: any): Promise<void> {
    patientData.patient.validToDate = moment().add(2, 'days').toDate();

    await this.saveInternal<IPatient>([patientData.patient], 'PatientWorkItem');
    await this.saveInternal<IPatientNote>(patientData.patientNotes, 'PatientNoteWorkItem');
    await this.saveInternal<AttachmentDto>(patientData.attachments, 'AttachmentWorkItem');
    await this.saveInternal<IAudit>(patientData.documents, 'AuditWorkItem');
    await this.saveInternal<IIntegratedCare>(patientData.integratedCares, 'IntegratedCareWorkItem');
    await this.saveInternal<ISingleOrder>(patientData.singleOrders, 'SingleOrderWorkItem');
    await this.saveInternal<IErpOrder>(patientData.erpOrders, 'ErpOrderWorkItem');

    let patientsToRemove = localStorage.getItem('patientsToRemove');
    if (patientsToRemove && patientsToRemove.includes(patientData.patient._id)) {
      return;
    }
    patientsToRemove = patientsToRemove ? `${patientsToRemove}, ` : '';
    localStorage.setItem('patientsToRemove', `${patientsToRemove}${patientData.patient._id}`);
  }

  private async saveInstitutionData(patientData: {
    doctor: IInstitution;
    hospital: IInstitution;
    nursingHome: IInstitution;
    nursingService: IInstitution;
    pharmacy: IInstitution;
  }): Promise<void> {
    await this.saveInternal<IDoctor>([patientData.doctor], 'DoctorWorkItem');
    await this.saveInternal<IHospital>([patientData.hospital], 'HospitalWorkItem');
    await this.saveInternal<INursingHome>([patientData.nursingHome], 'NursingHomeWorkItem');
    await this.saveInternal<INursingService>([patientData.nursingService], 'NursingServiceWorkItem');
    await this.saveInternal<IPharmacy>([patientData.pharmacy], 'PharmacyWorkItem');
  }

  private async saveInternal<T extends IModel>(models: IModel[], workItemName: string) {
    if (!models || !models.length || models.every(model => model == null)) {
      return;
    }

    const workItem: IDatabaseSynchronizer<T> = this._appController.getWorkItemByCtor(
      `${workItemName}`
    ) as IDatabaseSynchronizer<T>;

    if (!workItem) {
      return;
    }

    await workItem.sync(models, this._cancellationToken);
  }
}
